import React, { useContext, useEffect, useMemo, useState } from 'react';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Link,
  List,
  ListItem,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Box, Stack } from '@mui/system';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import DataLoadingError from '@components/common/DataLoadingError';
import { useConfirm } from '@components/common/hooks/useConfirm';
import useFormModal from '@components/common/hooks/useFormModal/useFormModal';
import useStaffUserLookup from '@components/common/hooks/useStaffUserLookup';
import useSessionDetailsQuery from '@components/common/hooks/api/useSessionDetailsQuery';
import TabList from '@components/common/TabList';
import { shouldTrackParticipants } from '@components/Pages/Events/eventFunctions';
import SessionParticipantsTable from '@components/Pages/Events/Sessions/SessionDetails/SessionParticipantsTable';
import useSessionParticipantsQuery from '@components/common/hooks/api/useSessionParticipantsQuery';
import { API_URLS, axiosRequest } from '@lib/axios';
import { PageContext } from '@lib/contexts/pageContext';
import UserContext from '@lib/contexts/userContext';
import moment from '@lib/moment';
import { AbilityContext, Subjects } from '@lib/permissions';
import { formatDateTime, scrollToAlert } from '@lib/utils';
import { getDateHeader, getSessionName } from '../sessionFunctions';
import SessionCancelModal from './SessionCancelModal';

const tabs = [
  {
    name: 'participants',
    label: 'PARTICIPANTS',
  },
];

const SessionDetails = ({ setTitle }) => {
  const [canceling, setCanceling] = useState(false);
  const [currentTab, setCurrentTab] = useState('participants');

  const { authenticated, userState: { userRecord }, featureFlags } = useContext(UserContext);

  const {
    setBreadcrumbValue,
    setPageMessage,
    appendMessages,
    setPageErrorMessage,
  } = useContext(PageContext);

  const { eventID, sessionID } = useParams();
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const formModal = useFormModal();
  const confirm = useConfirm();
  const theme = useTheme();
  const { getUserName } = useStaffUserLookup();

  const queryClient = useQueryClient();

  const participantsQuery = useSessionParticipantsQuery(eventID);

  const { data: session, isLoading, refetch, isError, error } = useSessionDetailsQuery(sessionID);

  // Restores session/event status to Incomplete. Used to undo Canceled or Completed status.
  const restoreMutation = useMutation({
    // Note: Changing session status can also cause the event status to change. That happens on the back end.
    mutationFn: async () => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.SESSIONS}/${sessionID}`,
      data: {
        status: 'INCOMPLETE',
      }
    }),
    onSuccess: async () => {
      setPageErrorMessage('');
      appendMessages('Session status restored to INCOMPLETE.');
      // Note: session.event.status will have the old value until the refetch below.
      if (session.event.status !== 'INCOMPLETE') {
        appendMessages('Event status restored to INCOMPLETE.');
      }
      await queryClient.invalidateQueries();
      await refetch();
      await participantsQuery.refetch();
    },
    onError: async (err) => {
      setPageErrorMessage(['Error saving session status', err]);
      console.log('[SessionDetails][undo] error:', err);
    },
    onSettled: scrollToAlert,
  });

  const cancelMutation = useMutation({
    mutationFn: async (updates) => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.SESSIONS}/${sessionID}`,
      data: updates,
    }),
    onSuccess: async () => {
      setPageMessage('Session Canceled');
      await queryClient.invalidateQueries();
      await refetch();
      await participantsQuery.refetch();
    },
    onError: async (err) => {
      setPageErrorMessage(['Error canceling session!', err]);
      console.log('[SessionDetails][cancel] error:', err);
    },
    onSettled: () => {
      scrollToAlert();
      setCanceling(false);
    },
  });

  useEffect(() => {
    if (session) {
      setBreadcrumbValue(':event', session.event);
      setBreadcrumbValue(':session', session);
    }
    // eslint-disable-next-line
  }, [session]);

  useEffect(() => {
    if (isError) {
      console.log('[SessionDetails] error fetching session:', error);
      setTitle('Error Accessing Page');
    }
  }, [error, isError, setTitle]);

  const handleCancel = async () => {
    const hasCompleted = session.completed;
    const cancelReason = await formModal({
      Form: SessionCancelModal,
      FormProps: {
        sessionId: session.id,
        hasCompleted,
        query: participantsQuery,
      },
      confirmationText: 'Cancel Session',
      cancellationText: 'Keep Session',
    })
      .then(({ cancel_reason = null, other_text = null }) => other_text || cancel_reason)
      .catch(() => null);

    if (cancelReason) {
      setCanceling(true);
      const updates = {
        status: 'CANCELED',
        cancel_reason: cancelReason,
        canceled_by: userRecord.email,
        canceledAt: moment().toISOString(true),
      };
      cancelMutation.mutate(updates);
    }
    return false;
  };

  const sessionName = getSessionName({
    session,
    dateFallback: true,
    useNill: false,
  });

  const getAudienceChip = () => {
    if (session.event.event_type !== 'CLASS') {
      return null;
    }
    switch (session.lesson?.audience) {
      case 'Adults':
        return <Chip label="Adult Program"/>;
      case 'Youth':
        return <Chip label="Youth Program"/>;
      case 'Family':
        return <Chip label="Family Program"/>;
      default:
        return null;
    }
  };

  const formatDeliverySite = () => session.delivery_location.replaceAll(/([^(]+)\(([^)]+)\)/g, '$1 | $2');

  const showParticipantFeatures = !!session?.event && featureFlags?.includes('participants') &&
    shouldTrackParticipants(session.event);

  const handleComplete = () => {
    if (moment().isBefore(session.mEnd)) {
      confirm({
        title: 'You cannot complete this session',
        content: (
          <Box sx={{ width: '100%' }} key="content">
            You cannot complete this session because it has not yet ended.
          </Box>
        ),
        confirmationText: 'Close',
        hideCancelButton: true,
      });
      return;
    }
    if (!session.sessionOrder.isFirstIncompleteSession && ability.cannot('complete_out_of_order', Subjects.SESSION)) {
      confirm({
        title: 'You cannot complete this session',
        content: (
          <Box sx={{ width: '100%' }} key="content">
            There are one or more previous sessions that are incomplete for this event. You must first complete
            or cancel all previous sessions before you can complete this session.
          </Box>
        ),
        confirmationText: 'Close',
        hideCancelButton: true,
      });
      return;
    }

    // If the participant list isn't finalized, send the user to the "Finalize Participants" page
    if (showParticipantFeatures && !session.is_finalized) {
      navigate(`/events/${session.event.id}/${sessionID}/participants`);
      return;
    }

    // Otherwise, they go directly to the "Complete Session" page.
    navigate(`/events/${session.event.id}/${sessionID}/complete`);
  };

  const handleUndoCancel = async () => {
    const undoConfirmed = await confirm({
      title: 'Undo Canceled Status',
      content: (
        <Box sx={{ width: '100%' }} key="content">
          {session.event.status === 'INCOMPLETE' && (
            <p>When you undo the canceled status for this session, it will change from Canceled to Incomplete and staff
              will be able to resume editing.</p>
          )}
          {session.event.status !== 'INCOMPLETE' && (
            <p>When you undo the canceled status for this session, the status for both the event and session will
              change to Incomplete and staff will be able to resume editing.</p>
          )}
          <p>Are you sure you want to undo the canceled status for this session?</p>
        </Box>
      ),
      confirmationText: 'Yes, Undo Canceled Status',
      cancellationText: 'No',
    })
      .then(() => true)
      .catch(() => false);

    if (undoConfirmed) {
      restoreMutation.mutate();
    }
  };

  const handleUndoComplete = async () => {
    const undoConfirmed = await confirm({
      title: 'Undo Completed Status',
      content: (
        <Box sx={{ width: '100%' }} key="content">
          {session.event.status === 'INCOMPLETE' && (
            <p>When you undo the completed status for this session, it will change from Completed to Incomplete and
              staff will be able to resume editing.</p>
          )}
          {session.event.status !== 'INCOMPLETE' && (
            <p>When you undo the completed status for this session, the status for both the event and session will
              change to Incomplete and staff will be able to resume editing.</p>
          )}
          <p>Are you sure you want to undo the completed status for this session?</p>
        </Box>
      ),
      confirmationText: 'Yes, Undo Completed Status',
      cancellationText: 'No',
    })
      .then(() => true)
      .catch(() => false);

    if (undoConfirmed) {
      restoreMutation.mutate();
    }
  };

  const canEdit = useMemo(() => {
    if (!session || !authenticated) {
      return false;
    }
    return ability.can('edit', subject(Subjects.SESSION, session));
  }, [ability, authenticated, session]);

  const incomplete = !['COMPLETED', 'CANCELED'].includes(session?.status);

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign="center">
        <CircularProgress/>
      </Grid>
    );
  }

  if (isError && error.status !== 404) {
    return <DataLoadingError title={'Failed to load session!'} errors={error.errors}/>;
  }

  if (!session) {
    return (
      <>
        You do not have access, or the session was not found.
      </>
    );
  }

  return (
    <div id="pagewrapper" className={'with-two-sidebar'} style={{ padding: 0 }}>
      <div id="content-wrapper" className={'event-details-card'}>
        {session.status === 'CANCELED' && (
          <Alert severity="warning" sx={{ marginBottom: '24px', position: 'relative' }} data-cy="banner-canceled">
            <Typography variant="body2">
              This session is canceled.
              <br/>
              Cancellation Reason: {session.cancel_reason}
              <br/>
              Canceled on: {formatDateTime(session.canceledAt)} by {session.canceled_by_names}
            </Typography>
            {canEdit && (
              <Button
                variant="contained"
                onClick={handleUndoCancel}
                sx={{
                  display: 'inline-block',
                  position: 'absolute',
                  right: '10px',
                  bottom: '10px',
                }}
                data-cy="button-undo-cancel"
              >
                Undo Canceled Status
              </Button>
            )}
          </Alert>
        )}
        {session.status === 'COMPLETED' && (
          <Alert severity="success" sx={{ marginBottom: '24px', position: 'relative' }} data-cy="banner-completed">
            <Typography variant="body2">
              This session is completed. <Link to={`/events/${session.event_id}/${session.id}/complete`}
                                               component={RouterLink}>View completed session details</Link>
              <br/>
              Completed on: {formatDateTime(session.completedAt)} by {session.completed_by_names}
            </Typography>
            {canEdit && (
              <Button
                variant="contained"
                onClick={handleUndoComplete}
                sx={{
                  display: 'inline-block',
                  position: 'absolute',
                  right: '10px',
                  bottom: '10px',
                }}
                data-cy="button-undo-complete"
              >
                Undo Completed Status
              </Button>
            )}
          </Alert>
        )}
        <Paper elevation={3}>
          <Box padding={3}>
            <Typography variant="h1" color="ilstorm.main" mb={0} data-cy="session-name">
              {sessionName}
            </Typography>
            <Typography variant="body1" sx={{
              ...theme.typography.h3,
              color: theme.palette.blue.main,
              mb: 4,
            }} data-cy="session-dates">
              {getDateHeader(session)}
            </Typography>
            <Stack direction="row" spacing={1} data-cy="chips">
              {session.delivery_method?.includes('In Person') && (
                <Chip label="In Person"/>
              )}
              {session.delivery_method?.includes('Virtual/Live Online') && (
                <Chip label="Virtual/Live Online"/>
              )}
              {!!session.sessionOrder && !!session.sessionOrder.order && (
                <Chip
                  label={`Session ${session.sessionOrder.order} of ${session.sessionOrder.totalNonCanceled}`}
                  data-cy="chips-session"
                />
              )}
              {getAudienceChip()}
            </Stack>
            <List dense={true}>
              {session.delivery_method?.includes('In Person') && (
                <ListItem disablePadding data-cy="delivery-location">
                  {formatDeliverySite()}
                </ListItem>
              )}
              <ListItem disablePadding data-cy="languages">
                Language(s): {session.delivery_language.join(', ')}
              </ListItem>
              <ListItem disablePadding>
                Session Code: {session.short_code}
              </ListItem>
              <ListItem disablePadding>
                Collaborators: {session.collaborators.length
                ? session.collaborators.map(c => getUserName(c.email)).sort((a, b) => a.localeCompare(b)).join(', ')
                : 'None'}
              </ListItem>
              <ListItem disablePadding>
                Last Modified: {`${session.updated_by_names}, ${formatDateTime(session.updatedAt)}`}
              </ListItem>
            </List>
            <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ marginTop: '20px' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={2}
              >
                <Button
                  variant="contained"
                  startIcon={<KeyboardArrowLeftIcon/>}
                  component={RouterLink}
                  to={`/events/${session.event.id}`}
                >
                  Back to Event
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon/>}
                  component={RouterLink}
                  disabled={!canEdit}
                  to={canEdit ? `/events/${session.event.id}/${sessionID}/edit` : 'javascript:void(0);'}
                >
                  Edit Session
                </Button>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={2}
              >
                {incomplete && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<ClearIcon/>}
                    disabled={canceling || ability.cannot('cancel', subject(Subjects.SESSION, session))}
                    onClick={handleCancel}
                    data-cy="cancel-button"
                  >
                    Cancel Session
                  </Button>
                )}
                {incomplete && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon/>}
                    onClick={handleComplete}
                    disabled={ability.cannot('complete', subject(Subjects.SESSION, session))}
                    data-cy="complete-button"
                  >
                    Complete Session
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </Paper>

        {showParticipantFeatures && (
          <>
            <TabList tabs={tabs} currentTab={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}/>

            {currentTab === 'participants' && (
              <SessionParticipantsTable session={session} query={participantsQuery}/>
            )}
          </>
        )}

      </div>
    </div>
  )
    ;
};

export default SessionDetails;
